import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Clock, Users } from "lucide-react";
import api from "@/api";
import TopBar from "@/components/TopBar";
import { useLang } from "@/context/LanguageContext";

// Phase F (Feb 2026): Management view of Present/Absent today.
export default function ManagementAttendance() {
  const nav = useNavigate();
  const { t, lang } = useLang();
  const [data, setData] = useState({ present: [], absent: [] });
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/attendance", { params: { date_str: date } })
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, [date]);

  return (
    <div className="min-h-screen bg-[#F5F5F7] pb-10 page-slide-in">
      <TopBar title={lang === "ta" ? "வரவு பதிவு" : "Attendance"} onBack={() => nav("/management")} />
      <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-5 fade-up">
        {/* Date filter */}
        <div className="bg-white rounded-2xl p-4 soft-shadow flex items-center gap-3">
          <label className="text-sm font-bold text-[#86868B]">{lang === "ta" ? "தேதி" : "Date"}:</label>
          <input
            data-testid="att-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            style={{ color: "#0A1A3F", WebkitTextFillColor: "#0A1A3F" }}
            className="bg-[#F5F5F7] rounded-xl px-3 py-2 text-sm font-semibold focus:bg-white focus:ring-2 focus:ring-[#0055FF]/30 outline-none"
          />
        </div>

        {/* Totals */}
        <div className="grid grid-cols-2 gap-3" data-testid="att-totals">
          <div className="bg-white rounded-2xl p-4 soft-shadow flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-[#34C759] grid place-items-center">
              <CheckCircle2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold">{lang === "ta" ? "வந்தவர்கள்" : "Present"}</div>
              <div className="heading-font text-3xl font-bold text-[#1D1D1F]">{data.present.length}</div>
            </div>
          </div>
          <div className="bg-white rounded-2xl p-4 soft-shadow flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-[#FF3B30] grid place-items-center">
              <XCircle className="w-6 h-6 text-white" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#86868B] font-bold">{lang === "ta" ? "வரல" : "Absent"}</div>
              <div className="heading-font text-3xl font-bold text-[#1D1D1F]">{data.absent.length}</div>
            </div>
          </div>
        </div>

        {/* Present list */}
        <div className="bg-white rounded-2xl soft-shadow overflow-hidden">
          <div className="p-5 border-b border-gray-100 flex items-center gap-2">
            <Users className="w-5 h-5 text-[#34C759]" />
            <h2 className="heading-font font-bold text-lg">{lang === "ta" ? "வரவு (Present)" : "Present Today"}</h2>
          </div>
          {loading ? (
            <div className="p-8 text-center text-[#86868B]">{t("loading")}</div>
          ) : data.present.length === 0 ? (
            <div className="p-8 text-center text-[#86868B]" data-testid="att-present-empty">
              {lang === "ta" ? "எவரும் வரல" : "Nobody checked in"}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50/60">
                <tr className="text-left text-[#86868B] uppercase tracking-wider text-xs">
                  <th className="px-4 py-3">{t("name")}</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">{lang === "ta" ? "வந்த நேரம்" : "Check-in"}</th>
                  <th className="px-4 py-3">{lang === "ta" ? "போன நேரம்" : "Check-out"}</th>
                  <th className="px-4 py-3">{lang === "ta" ? "மொத்தம்" : "Total"}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.present.map(p => (
                  <tr key={p.id} className="hover:bg-gray-50/40" data-testid={`att-present-${p.user_id}`}>
                    <td className="px-4 py-3 font-semibold heading-font">{p.user_name}</td>
                    <td className="px-4 py-3">
                      <span className={`pill ${p.role === "manager" ? "bg-blue-50 text-blue-700" : "bg-purple-50 text-purple-700"}`}>
                        {p.role === "manager" ? "Manager" : "Salesman"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs">{new Date(p.check_in_at).toLocaleTimeString()}</td>
                    <td className="px-4 py-3 text-xs">
                      {p.check_out_at ? new Date(p.check_out_at).toLocaleTimeString() : <span className="pill bg-orange-50 text-orange-700"><Clock className="w-3 h-3" /> {lang === "ta" ? "நடப்பு" : "Active"}</span>}
                    </td>
                    <td className="px-4 py-3 font-bold">
                      {p.total_minutes != null ? `${Math.floor(p.total_minutes/60)}h ${p.total_minutes%60}m` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Absent list */}
        <div className="bg-white rounded-2xl soft-shadow overflow-hidden">
          <div className="p-5 border-b border-gray-100 flex items-center gap-2">
            <XCircle className="w-5 h-5 text-[#FF3B30]" />
            <h2 className="heading-font font-bold text-lg">{lang === "ta" ? "வரவில்லை (Absent)" : "Absent Today"}</h2>
          </div>
          {data.absent.length === 0 ? (
            <div className="p-8 text-center text-[#86868B]" data-testid="att-absent-empty">
              {lang === "ta" ? "எவரும் காணவில்லை — அருமை!" : "Everyone is here — great!"}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50/60">
                <tr className="text-left text-[#86868B] uppercase tracking-wider text-xs">
                  <th className="px-4 py-3">{t("name")}</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">{t("phone")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data.absent.map(u => (
                  <tr key={u.id} className="hover:bg-gray-50/40" data-testid={`att-absent-${u.id}`}>
                    <td className="px-4 py-3 font-semibold heading-font">{u.name}</td>
                    <td className="px-4 py-3">
                      <span className={`pill ${u.role === "manager" ? "bg-blue-50 text-blue-700" : "bg-purple-50 text-purple-700"}`}>
                        {u.role === "manager" ? "Manager" : "Salesman"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-[#86868B]">{u.phone || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
